import React, { useState, useEffect, useMemo } from 'react';
import { mediaUrl } from '../../utils/mediaUrl';
import api from '../../services/api';
import { AdminSidebar } from './AdminDashboardPage';
import AdminHeader from './AdminHeader';
import '../../css/adminexclusivestrip.css';
import { Plus, Pencil, Trash2, Loader2, AlertCircle, RectangleHorizontal, X } from 'lucide-react';
import { BannerThumbnail } from '../../components/admin/BannerThumbnail';
// Table.jsx lives at src/components/Table.jsx
import { Table } from '../../components/Table';
import { useToast } from '../../contexts/ToastContext';

const AdminExclusiveBannersPage = () => {
  const { confirm } = useToast();
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    image: '',
    link: '',
    order: 0,
    isActive: true
  });

  useEffect(() => {
    loadBanners();
  }, []);

  const loadBanners = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/exclusive-banners');
      setBanners(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Failed to load banners');
      console.error(err);
      setBanners([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingBanner) {
        await api.put(`/exclusive-banners/${editingBanner._id}`, formData);
      } else {
        await api.post('/exclusive-banners', formData);
      }
      await loadBanners();
      setShowModal(false);
      resetForm();
    } catch (err) {
      setError('Failed to save banner');
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    const ok = await confirm({
      title: 'Delete this banner?',
      message: 'The exclusive strip banner will be removed.',
      confirmLabel: 'Delete'
    });
    if (!ok) return;
    try {
      await api.delete(`/exclusive-banners/${id}`);
      await loadBanners();
    } catch (err) {
      setError('Failed to delete banner');
      console.error(err);
    }
  };

  const toggleStatus = async (id, currentStatus) => {
    try {
      await api.patch(`/exclusive-banners/${id}`, { isActive: !currentStatus });
      await loadBanners();
    } catch (err) {
      setError('Failed to update banner status');
      console.error(err);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      subtitle: '',
      image: '',
      link: '',
      ctaPrimaryLabel: '',
      ctaPrimaryLink: '',
      ctaSecondaryLabel: '',
      ctaSecondaryLink: '',
      order: 0,
      isActive: true
    });
    setEditingBanner(null);
  };

  const openModal = (banner = null) => {
    if (banner) {
      setFormData({
        title: banner.title,
        subtitle: banner.subtitle,
        image: banner.image,
        link: banner.link,
        order: banner.order,
        isActive: banner.isActive
      });
      setEditingBanner(banner);
    } else {
      resetForm();
    }
    setShowModal(true);
  };

  const columns = useMemo(
    () => [
      {
        Header: 'Order',
        accessor: 'order',
        Cell: ({ value }) => <span className="admin-order-badge">#{value ?? 0}</span>
      },
      { Header: 'Title', accessor: 'title' },
      { Header: 'Subtitle', accessor: 'subtitle' },
      {
        Header: 'Image',
        id: 'image',
        disableSortBy: true,
        Cell: ({ row }) => (
          <BannerThumbnail
            src={mediaUrl(row.original.image)}
            alt={row.original.title}
            title={row.original.title}
          />
        )
      },
      {
        Header: 'Status',
        id: 'status',
        Cell: ({ row }) => {
          const banner = row.original;
          return (
            <button
              className={`admin-status-pill ${banner.isActive ? 'active' : 'inactive'}`}
              onClick={() => toggleStatus(banner._id, banner.isActive)}
              title="Click to toggle status"
            >
              <span className="status-dot" />
              {banner.isActive ? 'Active' : 'Inactive'}
            </button>
          );
        }
      },
      {
        Header: 'Actions',
        id: 'actions',
        disableSortBy: true,
        Cell: ({ row }) => (
          <div className="admin-action-group">
            <button
              className="admin-btn-edit"
              onClick={() => openModal(row.original)}
              title="Edit Banner"
            >
              <Pencil size={13} /> Edit
            </button>
            <button
              className="admin-btn-delete"
              onClick={() => handleDelete(row.original._id)}
              title="Delete Banner"
            >
              <Trash2 size={13} /> Delete
            </button>
          </div>
        )
      }
    ],
    []
  );

  const pageHeading = (withAction) => (
    <div className="aeb-heading">
      <div>
        <h1 className="aeb-title">Exclusive Banners</h1>
        <p className="aeb-subtitle">Promotion strips that feature special offers across the storefront.</p>
      </div>
      {withAction && (
        <div className="aeb-heading-actions">
          <button type="button" className="aeb-btn-primary" onClick={() => openModal()}>
            <Plus size={16} /> Add New Banner
          </button>
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="admin-layout">
        <AdminSidebar />
        <div className="admin-content">
          <AdminHeader title="Exclusive Banners" />
          <div className="aeb-page">
            {pageHeading(false)}
            <div className="aeb-state">
              <Loader2 size={26} className="aeb-spinner" />
              <p className="aeb-state-text">Loading exclusive banners...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <div className="admin-content">
        <AdminHeader title="Exclusive Banners" />
        <div className="aeb-page">
          {pageHeading(true)}

          {error && (
            <div className="aeb-error-message" role="alert">
              <AlertCircle size={16} /> {error}
            </div>
          )}

          {banners.length === 0 ? (
            <div className="aeb-state aeb-state--empty">
              <span className="aeb-state-icon"><RectangleHorizontal size={20} /></span>
              <p className="aeb-state-title">No exclusive banners configured yet</p>
              <p className="aeb-state-text">Add exclusive promotion strips to feature special offers across the storefront.</p>
              <button type="button" className="aeb-btn-primary" onClick={() => openModal()}>
                <Plus size={16} /> Add First Banner
              </button>
            </div>
          ) : (
            <Table columns={columns} data={banners} show />
          )}
        </div>

        {showModal && (
          <div className="aeb-modal-overlay" onClick={() => setShowModal(false)}>
            <div className="aeb-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
              <div className="aeb-modal-head">
                <h2>{editingBanner ? 'Edit Banner' : 'Add New Banner'}</h2>
                <button type="button" className="aeb-modal-close" onClick={() => setShowModal(false)} aria-label="Close">
                  <X size={18} />
                </button>
              </div>
              <form onSubmit={handleSubmit} className="aeb-form">
                <div className="aeb-modal-body">
                  <div className="aeb-form-group">
                    <label>Title</label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    />
                  </div>
                  <div className="aeb-form-group">
                    <label>Subtitle</label>
                    <input
                      type="text"
                      value={formData.subtitle}
                      onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                    />
                  </div>
                  <div className="aeb-form-group">
                    <label>Image URL</label>
                    <input
                      type="url"
                      value={formData.image}
                      onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                      required
                    />
                  </div>
                  <div className="aeb-form-group">
                    <label>Link</label>
                    <input
                      type="url"
                      value={formData.link}
                      onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                    />
                  </div>
                  <div className="aeb-form-group">
                    <label>Order</label>
                    <input
                      type="number"
                      value={formData.order}
                      onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                      min="0"
                    />
                  </div>
                  <div className="aeb-checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={formData.isActive}
                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      />
                      Active
                    </label>
                  </div>
                </div>
                <div className="aeb-modal-actions">
                  <button type="button" className="aeb-btn-cancel" onClick={() => setShowModal(false)}>Cancel</button>
                  <button type="submit" className="aeb-btn-primary">Save</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminExclusiveBannersPage;
